"use client"
import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  createStationMarkers,
  customMarker,
} from "./utils/createStationMarkers"

// 0. context types:
type ItemWithLocation = {
  id: number
  location: { latitude: number /* double */; longitude: number /* double */ }
}

type MyContextData<T extends ItemWithLocation> = {
  selectedStore: T | null
  setSelectedStore: Dispatch<SetStateAction<T | null>>
  stores: T[]
  alphabetOrderStores: T[]
  setStores: Dispatch<SetStateAction<T[]>>
  setAlphabetOrderStores: Dispatch<SetStateAction<T[]>>
  filterStores: T[]
  setFilterStores: Dispatch<SetStateAction<T[]>>
  setMap: Dispatch<SetStateAction<google.maps.Map>>
  setMaps: Dispatch<SetStateAction<any>>
  onStoreClick: (store: T) => void
}

type MyProviderProps<Item extends ItemWithLocation> = {
  itemList: Item[]
}

// 1. create context + 2.naming context shows in component tree
export const MapContext = createContext<MyContextData<ItemWithLocation> | null>(
  null
)
MapContext.displayName = "MapContext"

// 3. context provider:
export function MapContextProvider<T extends ItemWithLocation>({
  children,
  itemList,
}: React.PropsWithChildren<MyProviderProps<T>>) {
  // keep track of `selectedItems` somehow
  // stores states:
  const [stores, setStores] = useState<T[]>(itemList || [])
  const [alphabetOrderStores, setAlphabetOrderStores] = useState<T[]>(
    itemList || []
  )
  const [filterStores, setFilterStores] = useState<T[]>(itemList || [])
  const [selectedStore, setSelectedStore] = useState<T | null>(null)
  // map states:
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [maps, setMaps] = useState<any>(null)
  const [activeMarkers, setActiveMarkers] = useState<customMarker[]>([])

  // clear markers function:
  const clearMarkers = useCallback(() => {
    activeMarkers.forEach((marker) => {
      marker.setMap(null)
    })
  }, [activeMarkers])
  // on showRoom click function:
  const onStoreClick = (store: T, isMarker: boolean = false) => {
    setSelectedStore(store)
    if (map && maps && store && !isMarker) {
      map.setCenter({
        lat: store.location.latitude,
        lng: store.location.longitude,
      })
      // map.setZoom(15)
      const marker = activeMarkers.find((item) => item.tagId === store.id)
      if (marker) {
        maps.event.trigger(marker, "click")
      }
    }
  }

  // creating the map markers:
  useEffect(() => {
    if (!filterStores?.length || !map || !maps) return
    clearMarkers()
    setActiveMarkers(
      createStationMarkers(maps, filterStores, map, onStoreClick)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStores, map, maps])

  return (
    <MapContext.Provider
      value={{
        selectedStore,
        setSelectedStore,
        stores: stores || [],
        setStores,
        setMap,
        setMaps,
        onStoreClick,
        filterStores: filterStores || [],
        setFilterStores,
        setAlphabetOrderStores,
        alphabetOrderStores: alphabetOrderStores || [],
      }}
    >
      {children}
    </MapContext.Provider>
  )
}

export function useMapContext<T extends ItemWithLocation>() {
  const context = useContext<MyContextData<T>>(
    MapContext as unknown as React.Context<MyContextData<T>>
  )
  if (!context) {
    throw new Error("useMyContext must be used under MyContextProvider")
  }
  return context
}
